<script setup>
import FormInput from "../ui-components/FormInput.vue";
import LocationBlock from "../ui-components/LocationBlock.vue";
import {Swiper, SwiperSlide} from "swiper/vue";
import LocationBlockSingular from "../ui-components/LocationBlockSingular.vue";
import {computed, watch} from "vue";
import {VueTelInput} from 'vue-tel-input';
import 'vue-tel-input/vue-tel-input.css';

const props = defineProps({
    locations: Array,
    theme: Object,
    currentBrightness: Number,
    noForm: Boolean,
    neoDesign: Boolean,
})

const form = defineModel('form');
const valid = defineModel('valid');

const customizationClasses = computed(() => {
    return [props.theme.roundness < 3 ? 'border-b' : '', props.theme.darkMode ? 'border-neutral-700' : '', props.currentBrightness < 140 ? 'text-white' : 'text-black'];
})
const containerClasses = computed(() => {
    return {
        'rounded-xl': props.theme.roundness === 1,
        'rounded-2xl': props.theme.roundness === 2,
        'rounded-3xl': props.theme.roundness === 3,
        'bg-transparent border-neutral-700': props.theme.darkMode
    };
})

const needsPhone = computed(() => {
    const ids = props.locations.filter((item) => item.service === 'phone_call' || item.service === 'whatsapp').map(item => item.id);
    return ids.includes(form.value.id_reservation_locations);
})

watch(() => form.value, (now) => {
    //
}, {deep: true});
</script>

<template>
    <!-- ── Neo design ──────────────────────────────────────────────────────── -->
    <div v-if="neoDesign" class="nd-fset">
        <!-- Location selection -->
        <template v-if="!noForm && locations && locations.length">
            <h4>{{ $t('widget.location') }}</h4>
            <div class="nd-location-wrap">
                <swiper v-if="locations.length > 1" :space-between="8" :breakpoints="{
                    768: { slidesPerView: locations.length > 3 ? 3.25 : Math.min(locations.length, 3) },
                    0:   { slidesPerView: 1.15 }
                }">
                    <swiper-slide v-for="location in locations" :key="location.id">
                        <location-block v-model="form.id_reservation_locations" :location="location" :theme="theme" :theming="containerClasses"/>
                    </swiper-slide>
                </swiper>
                <location-block-singular
                    v-else-if="locations.length > 0"
                    v-model="form.id_reservation_locations"
                    :location="locations[0]"
                    :theme="theme"
                    :theming="containerClasses"
                />
                <small v-if="form.invalid('id_reservation_locations')" class="nd-err">{{ form.errors['id_reservation_locations'] }}</small>
            </div>
        </template>

        <!-- Contact fields -->
        <h4>{{ $t('widget.form') }}</h4>

        <div class="nd-field">
            <label>{{ $t('widget.name_label') }} <span class="nd-req">*</span></label>
            <input class="nd-input" type="text" v-model="form.name" autocomplete="name"/>
            <small v-if="form.errors.name" class="nd-err">{{ form.errors.name }}</small>
        </div>

        <div class="nd-field-row">
            <div class="nd-field">
                <label>{{ $t('widget.email_label') }} <span class="nd-req">*</span></label>
                <input class="nd-input" type="email" v-model="form.email" autocomplete="email"/>
                <small v-if="form.errors.email" class="nd-err">{{ form.errors.email }}</small>
            </div>
            <div class="nd-field">
                <label>{{ $t('widget.phone_label') }}<span v-if="needsPhone" class="nd-req"> *</span></label>
                <vue-tel-input
                    class="nd-tel"
                    mode="international"
                    :preferredCountries="['CZ','SK','DE','PL','AT']"
                    :inputOptions="{ placeholder: '+420', showDialCode: false }"
                    v-model="form.phone"
                />
                <small v-if="form.errors.phone" class="nd-err">{{ form.errors.phone }}</small>
            </div>
        </div>

        <div class="nd-field">
            <label>{{ $t('widget.note_label') }}</label>
            <textarea class="nd-input nd-textarea" v-model="form.note" rows="3"/>
        </div>

        <!-- Dynamic formBuilder fields -->
        <template v-for="input in $page.props.formBuilder" :key="input.id">
            <div class="nd-field">
                <label>{{ input.label }}<span v-if="input.required" class="nd-req"> *</span></label>
                <input
                    v-if="input.type === 'text' || input.type === 'mask'"
                    class="nd-input"
                    type="text"
                    v-model="form.formdata[input.id]"
                />
                <textarea
                    v-else-if="input.type === 'textarea'"
                    class="nd-input nd-textarea"
                    v-model="form.formdata[input.id]"
                    rows="3"
                />
                <select
                    v-else-if="input.type === 'list' || input.type === 'select'"
                    class="nd-input nd-select"
                    v-model="form.formdata[input.id]"
                >
                    <option v-for="opt in input.options" :key="opt.value ?? opt.label" :value="opt.value ?? opt.label">{{ opt.label }}</option>
                </select>
                <form-input
                    v-else
                    :type="input.type"
                    :options="input.options"
                    v-model="form.formdata[input.id]"
                    :invalid="form.errors['formdata.' + input.id]"
                    :required="!!input.required"
                />
                <small v-if="form.errors['formdata.' + input.id]" class="nd-err">{{ form.errors['formdata.' + input.id] }}</small>
            </div>
        </template>
    </div>

    <!-- ── Original design ─────────────────────────────────────────────────── -->
    <div v-else>
        <section v-if="!noForm && locations && locations.length" id="location" class="px-5 mb-4">
            <div class="flex items-baseline mt-5 gap-2 text-lg pb-2" :class="customizationClasses">
                <i class="pi pi-map-marker text-gray-400" style="font-size: 1.25rem"></i>
                {{ $t('widget.location') }}
            </div>
            <div class="mt-4">
                <swiper v-if="locations.length > 1" :space-between="8" :breakpoints="{
                    1024: { slidesPerView: locations.length > 3 ? 3.25 : 3 },
                    768:  { slidesPerView: locations.length > 3 ? 3.25 : 3 },
                    640:  { slidesPerView: locations.length > 1 ? 1.25 : 1 },
                    320:  { slidesPerView: locations.length > 1 ? 1.25 : 1, spaceBetween: 4 }
                }">
                    <swiper-slide v-for="location in locations" :key="location.id">
                        <location-block v-model="form.id_reservation_locations" :location="location" :theme="theme" :theming="containerClasses"/>
                    </swiper-slide>
                </swiper>
                <location-block-singular v-else-if="locations.length > 0" v-model="form.id_reservation_locations" :location="locations[0]" :theme="theme" :theming="containerClasses"/>
            </div>
            <small v-if="form.invalid('id_reservation_locations')" id="timeslot-error" class="text-red-500">{{ form.errors['id_reservation_locations'] }}</small>
        </section>
        <section v-if="!noForm" id="form" class="px-5 mb-4">
            <div class="flex items-baseline mt-5 gap-2 text-lg pb-2" :class="customizationClasses">
                <i class="pi pi-info text-gray-400" style="font-size: 1.25rem"></i>
                {{ $t('widget.form') }}
            </div>
            <div class="flex justify-center gap-6 mt-4 dark">
                <div class="grid md:grid-cols-2 gap-y-2 gap-x-4 grow">
                    <form-input :label="$t('widget.name_label')" v-model="form.name" :invalid="form.errors['name']" required type="text" :text-color="currentBrightness < 140 ? 'text-white': 'text-black'"/>
                    <form-input :label="$t('widget.note_label')" class="row-span-2" v-model="form.note" rows="4" type="textarea" :text-color="currentBrightness < 140 ? 'text-white': 'text-black'"/>
                    <form-input :label="$t('widget.email_label')" required v-model="form.email" :invalid="form.errors['email']" type="text" :text-color="currentBrightness < 140 ? 'text-white': 'text-black'"/>
                    <form-input :label="$t('widget.phone_label')" :required="needsPhone" v-model="form.phone" :invalid="form.errors['phone']" type="phone" :text-color="currentBrightness < 140 ? 'text-white': 'text-black'"/>
                    <form-input v-for="input in $page.props.formBuilder" :key="input" :type="input.type" :options="input.options"
                                :label="input.label" :required="!!input.required" v-model="form.formdata[input.id]" :invalid="form.errors['formdata.' + input.id]"
                                :text-color="currentBrightness < 140 ? 'text-white': 'text-black'"/>
                </div>
            </div>
        </section>
    </div>
</template>

<style scoped>
/* ── Neo design tokens (fall back gracefully when used outside .rw-page) ── */
.nd-fset {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0 0px 16px;
    font-family: var(--rw-font-display, "Bricolage Grotesque", ui-sans-serif, sans-serif);
}

.nd-fset h4 {
    font-family: var(--rw-font-display, "Bricolage Grotesque", ui-sans-serif, sans-serif);
    font-size: 16px;
    font-weight: 600;
    letter-spacing: -0.01em;
    margin: 0;
    color: var(--rw-ink, #0F1115);
}

.nd-location-wrap {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.nd-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.nd-field label {
    font-size: 12px;
    font-weight: 600;
    color: var(--rw-ink-2, #3A3F47);
}

.nd-req {
    color: #ef4444;
}

.nd-field-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
}

.nd-input {
    width: 100%;
    height: 44px;
    padding: 0 14px;
    border: 1px solid var(--rw-line, #E5E2DC);
    border-radius: var(--rw-r-md, 12px);
    font-size: 14px;
    font-family: inherit;
    background: var(--rw-paper, #fff);
    color: var(--rw-ink, #0F1115);
    outline: none;
    transition: border-color 0.12s, box-shadow 0.12s;
    box-sizing: border-box;
    -webkit-appearance: none;
    appearance: none;
}

.nd-input:focus {
    border-color: var(--brand, #2A6FDB);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand, #2A6FDB) 15%, transparent);
}

.nd-textarea {
    height: auto;
    padding: 12px 14px;
    resize: vertical;
    min-height: 88px;
}

.nd-select {
    cursor: pointer;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%236B7280' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 14px center;
    padding-right: 38px;
}

/* VueTelInput in neo mode — root element IS .nd-tel, :deep() needed for internals only */
.nd-tel {
    border: 1px solid var(--rw-line, #E5E2DC) !important;
    border-radius: var(--rw-r-md, 12px) !important;
    height: 44px;
    background: var(--rw-paper, #fff) !important;
    font-size: 14px;
    font-family: inherit;
    color: var(--rw-ink, #0F1115);
    transition: border-color 0.12s, box-shadow 0.12s;
    box-shadow: none !important;
    outline: none !important;
}

.nd-tel:focus-within {
    border-color: var(--brand, #2A6FDB) !important;
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand, #2A6FDB) 15%, transparent) !important;
}

.nd-tel :deep(.vti__dropdown) {
    border-right: 1px solid var(--rw-line, #E5E2DC);
    border-radius: var(--rw-r-md, 12px) 0 0 var(--rw-r-md, 12px);
    padding: 0 10px;
    background: transparent;
}

.nd-tel :deep(.vti__input) {
    background: transparent !important;
    color: var(--rw-ink, #0F1115);
    font-size: 14px;
    font-family: inherit;
    padding: 0 14px 0 8px;
    height: 100%;
    outline: none;
}

.nd-err {
    color: #ef4444;
    font-size: 12px;
}

@media (max-width: 480px) {
    .nd-field-row {
        grid-template-columns: 1fr;
    }
}
</style>
